import {
  FormantBotAvatarSchema,
  formantBotAvatarFromSeed,
  type FormantBotAvatar,
} from '@cortex/types';
import { useEffect, useState } from 'react';

/** Points of one oscilloscope trace, left to right, in viewBox space. */
export function oscilloscopePoints(
  avatar: FormantBotAvatar,
  phase: number,
  line: number,
  samples = 80,
): string {
  const pts: string[] = [];
  // Saved amplitude is 0.15–0.95. Map it onto most of the face so a 22px
  // avatar still shows a wave instead of a flat hairline.
  const swing = (7 + avatar.amplitude * 10) * (1 - line * 0.12);
  const cycles = avatar.cycles * (1 + line * 0.18);
  for (let i = 0; i < samples; i++) {
    const t = i / (samples - 1);
    const angle = t * cycles * Math.PI * 2 + phase + line * 1.3;
    const wave = Math.sin(angle) + Math.sin(angle * 3 + 0.4) * avatar.harmonic * 0.35;
    const x = t * 32 + 4;
    const y = 20 - wave * swing * 0.62;
    pts.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  }
  return pts.join(' ');
}

function traceHues(avatar: FormantBotAvatar): number[] {
  if (avatar.lines && avatar.lines.length >= 2) return avatar.lines.slice(0, 2);
  return [avatar.hue, (avatar.hue + 120) % 360];
}

/** Metaphysics accent tokens. Hue only picks which one; the paint is the token. */
const traceTokens = [
  { hue: 150, token: 'var(--horizon-glow)' },
  { hue: 120, token: 'var(--horizon-glow-hi)' },
  { hue: 190, token: 'var(--accent-teal)' },
  { hue: 245, token: 'var(--interface-iris)' },
  { hue: 28, token: 'var(--terminal-amber)' },
  { hue: 8, token: 'var(--accent-rose)' },
  { hue: 100, token: 'var(--accent-mint)' },
  { hue: 285, token: 'var(--accent-lavender)' },
] as const;

function traceToken(hue: number): string {
  const h = ((hue % 360) + 360) % 360;
  let best: (typeof traceTokens)[number] | undefined = traceTokens[0];
  let bestDist = 360;
  for (const stop of traceTokens) {
    const raw = Math.abs(h - stop.hue);
    const dist = Math.min(raw, 360 - raw);
    if (dist < bestDist) {
      best = stop;
      bestDist = dist;
    }
  }
  return best?.token ?? 'var(--horizon-glow)';
}

/** Drift the saved phase. About one full turn every three seconds. */
function useSlowPhase(base: number): number {
  const [phase, setPhase] = useState(base);
  useEffect(() => {
    setPhase(base);
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      setPhase((p) => p + dt * 2.1);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [base]);
  return phase;
}

export function resolveBotAvatar(stored: unknown, seed: string): FormantBotAvatar {
  const parsed = FormantBotAvatarSchema.safeParse(stored);
  return parsed.success ? parsed.data : formantBotAvatarFromSeed(seed);
}

/** Square oscilloscope face. `size` is the rendered pixel width. */
export function BotAvatar({
  avatar,
  size = 28,
  title,
}: {
  avatar: FormantBotAvatar;
  size?: number;
  title?: string;
}) {
  const phase = useSlowPhase(avatar.phase);
  const hues = traceHues(avatar);
  return (
    <svg
      className="bot-avatar"
      width={size}
      height={size}
      viewBox="0 0 40 40"
      role="img"
      aria-label={title ?? 'Bot avatar'}
    >
      <rect width="40" height="40" rx="9" fill="var(--bg)" />
      <rect
        x="0.4"
        y="0.4"
        width="39.2"
        height="39.2"
        rx="8.6"
        fill="none"
        stroke="var(--fog-green)"
        strokeWidth="0.6"
      />
      <line x1="5" y1="20" x2="35" y2="20" stroke="var(--ethereal-teal)" strokeWidth="0.4" />
      {hues.map((hue, line) => {
        const points = oscilloscopePoints(avatar, phase, line);
        const stroke = traceToken(hue);
        const glow = `color-mix(in oklab, ${stroke} ${Math.round(22 + avatar.glow * 24)}%, transparent)`;
        return (
          <g key={line}>
            <polyline
              points={points}
              fill="none"
              stroke={glow}
              strokeWidth="2.2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
            <polyline
              points={points}
              fill="none"
              stroke={stroke}
              strokeWidth="1.05"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        );
      })}
    </svg>
  );
}
